import {
  type AskPunttProjectFile,
  type AskPunttProjectFileVersion,
} from "@mg/schemas/src/christo/askPunttProject";
import {
  CaretDown,
  FileText,
  ImageSquare,
  PencilSimple,
  VideoCamera,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useState } from "react";

/**
 * Right-hand "PROJECT CONTEXT" panel for the project workspace. Renders the
 * project's versioned files (read-only in v1) — the same artifacts the agent
 * is given as project context. Each file shows its version stack with the
 * current version highlighted and the latest version's label as a "what
 * changed" note. Owners get a pencil affordance to edit the project's
 * description / metadata.
 */
/** A review/context asset that lives on a ticket rather than as a project file. */
export interface CampaignExtraDocument {
  source: string;
  name: string;
  type: string;
  kind: "review" | "context";
}

export function CampaignDocsPanel({
  files,
  extraDocuments = [],
  description,
  onEdit,
}: {
  files: AskPunttProjectFile[];
  /**
   * Files attached to the project's review tickets (review assets + context
   * assets) that are not also standalone project files. Rendered as simple
   * rows below the versioned project files.
   */
  extraDocuments?: CampaignExtraDocument[];
  /** The project's overall description, surfaced above the documents. */
  description?: string;
  /** Owner-only edit affordance for the project description / metadata. */
  onEdit?: () => void;
}) {
  const [open, setOpen] = useState(true);
  const totalCount = files.length + extraDocuments.length;

  return (
    <aside
      className="flex min-h-0 flex-1 flex-col bg-puntt-neutral-gray-1"
      aria-label="Project context"
      data-testid="project-context-panel"
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-5">
        <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11">
          Project context
        </h2>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label="Edit project context"
            data-testid="project-context-edit-button"
            className="flex size-7 items-center justify-center rounded-md text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
          >
            <PencilSimple size={15} />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        {description && (
          <div
            className="mb-4 rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-3.5"
            data-testid="campaign-project-description"
          >
            <Text size="1" weight="bold" className="text-puntt-neutral-gray-11">
              About this project
            </Text>
            <p className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-12">
              {description}
            </p>
          </div>
        )}
        <div className="overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label="Toggle documents and context"
            data-testid="campaign-docs-toggle"
            className="flex w-full items-center gap-2.5 px-3.5 py-3 transition-colors hover:bg-puntt-neutral-gray-2"
          >
            <Text size="2" weight="bold" className="text-puntt-neutral-gray-12">
              Documents &amp; context
            </Text>
            <span className="font-mono text-[11px] text-puntt-neutral-gray-11">
              {totalCount}
            </span>
            <span className="ml-auto text-puntt-neutral-gray-11">
              <CaretDown
                size={14}
                className={`transition-transform ${open ? "" : "-rotate-90"}`}
              />
            </span>
          </button>

          {open && (
            <div
              className="border-t border-puntt-neutral-gray-5 px-3.5 pb-3.5 pt-2"
              data-testid="campaign-docs-body"
            >
              {totalCount === 0 ? (
                <p
                  className="py-2 text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                  data-testid="campaign-docs-empty"
                >
                  No documents yet. Files added to this project — artwork under
                  review and reference material — show up here with their full
                  version history.
                </p>
              ) : (
                <>
                  {files.map((file) => (
                    <FileCard key={file.fileId} file={file} />
                  ))}
                  {extraDocuments.map((doc, idx) => (
                    <ExtraDocRow
                      key={`extra-${doc.source}`}
                      doc={doc}
                      idx={idx}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function ExtraDocRow({
  doc,
  idx,
}: {
  doc: CampaignExtraDocument;
  idx: number;
}) {
  const TypeIcon =
    doc.type === "image"
      ? ImageSquare
      : doc.type === "video"
        ? VideoCamera
        : FileText;
  return (
    <div
      className="mb-2 flex items-center gap-3 rounded-lg border border-puntt-neutral-gray-5 bg-base-white p-3 last:mb-0"
      data-testid={`campaign-extra-doc-${idx}`}
    >
      <span
        aria-hidden="true"
        className="flex size-10 shrink-0 items-center justify-center rounded-md bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
      >
        <TypeIcon size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium text-puntt-neutral-gray-12">
          {doc.name}
        </div>
        <div className="mt-0.5 text-xs capitalize text-puntt-neutral-gray-11">
          {doc.kind === "context" ? "context asset" : "review file"}
        </div>
      </div>
    </div>
  );
}

function latestVersion(
  file: AskPunttProjectFile,
): AskPunttProjectFileVersion | undefined {
  return (
    file.versions.find((v) => v.versionNumber === file.currentVersion) ??
    file.versions[file.versions.length - 1]
  );
}

function FileCard({ file }: { file: AskPunttProjectFile }) {
  const latest = latestVersion(file);
  const TypeIcon =
    file.type === "image"
      ? ImageSquare
      : file.type === "video"
        ? VideoCamera
        : FileText;

  return (
    <div
      className="mb-2 flex flex-col gap-2.5 rounded-lg border border-puntt-neutral-gray-5 bg-base-white p-3 last:mb-0"
      data-testid={`campaign-file-card-${file.fileId}`}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex size-10 shrink-0 items-center justify-center rounded-md bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
        >
          <TypeIcon size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-puntt-neutral-gray-12">
            {file.name}
          </div>
          <div className="mt-0.5 text-xs text-puntt-neutral-gray-11">
            {file.versions.length} version
            {file.versions.length === 1 ? "" : "s"}
          </div>
        </div>
        <div
          className="flex shrink-0 gap-1"
          data-testid={`campaign-file-versions-${file.fileId}`}
        >
          {file.versions.map((v) => {
            const current = v.versionNumber === file.currentVersion;
            return (
              <span
                key={v.versionId}
                className={`font-mono rounded border px-1.5 py-px text-[10px] font-medium ${
                  current
                    ? "border-puntt-neutral-gray-12 bg-puntt-neutral-gray-12 text-base-white"
                    : "border-puntt-neutral-gray-7 bg-base-white text-puntt-neutral-gray-11"
                }`}
                data-testid={`campaign-file-version-${file.fileId}-v${v.versionNumber}`}
              >
                v{v.versionNumber}
              </span>
            );
          })}
        </div>
      </div>

      {latest?.label && (
        <div className="flex items-start gap-2 rounded-md border-l-2 border-puntt-neutral-gray-10 bg-puntt-neutral-gray-2 px-2.5 py-2">
          <span className="font-mono shrink-0 pt-px text-[10px] text-puntt-neutral-gray-11">
            v{latest.versionNumber} ←
          </span>
          <span className="text-[12.5px] leading-snug text-puntt-neutral-gray-12">
            {latest.label}
          </span>
        </div>
      )}
    </div>
  );
}
